import * as React from 'react'
import { cn } from '../../../../lib/utils'
import Icon from '../../Icon'
import { Button } from '../../Button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../Collapsible'
import type { CalendarSyncSettingsProps, CalendarSyncDirection } from './types'

export type CalendarSyncPanelProps = CalendarSyncSettingsProps & {
  className?: string
}

const directionOptions: Array<{
  key: CalendarSyncDirection
  label: string
}> = [
  { key: 'push', label: 'Push to Google' },
  { key: 'pull', label: 'Pull from Google' },
  { key: 'both', label: 'Two-way' },
]

export function CalendarSyncPanel(props: CalendarSyncPanelProps) {
  const {
    className,
    isConnected = false,
    connectedAccount,
    connectedCalendars = [],
    availableCalendars = [],
    selectedCalendarIds = [],
    syncEnabled = false,
    syncDirection = 'both',
    externalReadOnly = true,
    authError,
    onConnect,
    onDisconnect,
    onCalendarsChange,
    onSyncEnabledChange,
    onSyncDirectionChange,
    onExternalReadOnlyChange,
  } = props

  const calendarsToShow =
    availableCalendars.length > 0 ? availableCalendars : connectedCalendars

  const toggleCalendar = (calendarId: string) => {
    const isSelected = selectedCalendarIds.includes(calendarId)
    const next = isSelected
      ? selectedCalendarIds.filter((id) => id !== calendarId)
      : [...selectedCalendarIds, calendarId]
    onCalendarsChange?.(next)
  }

  return (
    <div className={cn('w-full flex flex-col border-t mt-2 pt-2', className)}>
      <Collapsible defaultOpen={!isConnected}>
        <CollapsibleTrigger asChild>
          <div className="relative h-7 px-3 inline-flex items-center gap-2 rounded-md text-2xs bg-background hover:bg-accent w-full justify-between">
            <span className="flex items-center gap-2 min-w-0">
              <Icon name="CalendarSync" size={12} />
              <span className="truncate">Calendar sync</span>
            </span>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide',
                isConnected
                  ? 'bg-primary/10 text-primary'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {isConnected ? 'Connected' : 'Not connected'}
            </span>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="p-1 flex flex-col gap-2">
          {!isConnected ? (
            <div className="flex flex-col gap-2 px-1 py-1">
              <p className="text-2xs text-muted-foreground leading-relaxed">
                Connect Google Calendar to show external events alongside CRM
                records.
              </p>
              {authError ? (
                <p className="text-2xs text-destructive">{authError}</p>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                label="Connect Google Calendar"
                leftIcon="Link"
                handleClick={() => onConnect?.()}
                disabled={!onConnect}
                className="w-full"
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2 px-1 py-1">
              {connectedAccount ? (
                <div className="flex items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/20 px-2 py-1.5">
                  <span className="text-2xs truncate">{connectedAccount}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    label="Disconnect"
                    handleClick={() => onDisconnect?.()}
                    disabled={!onDisconnect}
                  />
                </div>
              ) : null}

              <label className="flex items-center justify-between gap-2 px-1">
                <span className="text-2xs">Enable sync</span>
                <input
                  type="checkbox"
                  checked={syncEnabled}
                  onChange={(e) => onSyncEnabledChange?.(e.target.checked)}
                  disabled={!onSyncEnabledChange}
                  className="h-3.5 w-3.5 rounded border-border"
                />
              </label>

              <div className="flex flex-col gap-1">
                <span className="text-2xs text-muted-foreground px-1">
                  Direction
                </span>
                <div className="grid grid-cols-1 gap-1">
                  {directionOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      disabled={!onSyncDirectionChange}
                      onClick={() => onSyncDirectionChange?.(opt.key)}
                      className={cn(
                        'flex items-center gap-2 rounded-md px-2 py-1.5 text-2xs transition-colors text-left',
                        syncDirection === opt.key
                          ? 'bg-accent text-foreground'
                          : 'hover:bg-accent/60 text-muted-foreground',
                      )}
                    >
                      {syncDirection === opt.key ? (
                        <Icon name="Check" size={12} />
                      ) : (
                        <span className="w-3" />
                      )}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex items-center justify-between gap-2 px-1">
                <span className="text-2xs">External events read-only</span>
                <input
                  type="checkbox"
                  checked={externalReadOnly}
                  onChange={(e) => onExternalReadOnlyChange?.(e.target.checked)}
                  disabled={!onExternalReadOnlyChange}
                  className="h-3.5 w-3.5 rounded border-border"
                />
              </label>

              {calendarsToShow.length > 0 ? (
                <div className="flex flex-col gap-1">
                  <span className="text-2xs text-muted-foreground px-1">
                    Calendars
                  </span>
                  {calendarsToShow.map((cal) => {
                    const selected = selectedCalendarIds.includes(cal.id)
                    return (
                      <button
                        key={cal.id}
                        type="button"
                        disabled={!onCalendarsChange}
                        onClick={() => toggleCalendar(cal.id)}
                        className={cn(
                          'flex items-center gap-2 rounded-md px-2 py-1.5 text-2xs transition-colors text-left',
                          selected
                            ? 'bg-accent text-foreground'
                            : 'hover:bg-accent/60 text-muted-foreground',
                        )}
                      >
                        <span
                          className="h-2 w-2 rounded-full shrink-0"
                          style={{
                            backgroundColor: cal.color ?? 'var(--primary)',
                          }}
                        />
                        <span className="truncate flex-1">{cal.name}</span>
                        {selected ? <Icon name="Check" size={12} /> : null}
                      </button>
                    )
                  })}
                </div>
              ) : null}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

export default CalendarSyncPanel
